import React from "react";
import { useState, useEffect, useCallback } from 'react';
import {
  onFullCatalog,
  getCategoryMeta,
  setCategoryMeta,
  enableBread,
  disableBread,
  updateBread,
} from '../../../services/breadCatalog.service';
import BreadCatalogList from './BreadCatalogList';
import BreadCatalogDetail from './BreadCatalogDetail';
import './BreadCatalog.css';

export default function BreadCatalogTab() {
  const [items, setItems] = useState([]);
  const [categoryMeta, setCategoryMetaState] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedBread, setSelectedBread] = useState(null);
  const [toast, setToast] = useState(null);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [newCatNameEs, setNewCatNameEs] = useState('');
  const [newCatNameEn, setNewCatNameEn] = useState('');
  const [newCatIcon, setNewCatIcon] = useState('🍞');
  const [newCatPrice, setNewCatPrice] = useState('');
  const [editingCatKey, setEditingCatKey] = useState(null);
  const [editCatPrice, setEditCatPrice] = useState('');

  // ── Load data ──────────────────────────────────────────────────

  const loadCategoryMeta = useCallback(async () => {
    try {
      const meta = await getCategoryMeta();
      setCategoryMetaState(meta);
    } catch (err) {
      console.error('Error loading category meta:', err);
    }
  }, []);

  useEffect(() => {
    loadCategoryMeta();
    const unsub = onFullCatalog((newItems) => {
      setItems(newItems);
      setLoading(false);
    });
    return unsub;
  }, [loadCategoryMeta]);

  // ── Toast ──────────────────────────────────────────────────────

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2000);
  }, []);

  // ── Navigation ─────────────────────────────────────────────────

  const handleSelect = (bread) => setSelectedBread(bread);
  const handleBack = () => setSelectedBread(null);
  const handleNew = () => setSelectedBread('new');

  const handleSaved = () => {
    loadCategoryMeta();
    if (selectedBread === 'new') {
      setSelectedBread(null);
    }
  };

  // ── Toggle enabled/disabled from list ──────────────────────────

  const handleToggleEnabled = async (bread) => {
    try {
      if (bread.enabled) {
        await disableBread(bread.id);
        showToast('Pan desactivado');
      } else {
        await enableBread(bread.id);
        showToast('Pan activado');
      }
    } catch (err) {
      console.error('Error toggling bread:', err);
      showToast('Error: ' + err.message);
    }
  };

  // ── Unique categories for filter dropdown ─────────────────────

  // Include categories from both the catalog (in case some breads reference a
  // category not yet in meta) and from categoryMeta (so newly created empty
  // categories still appear in the filter dropdown).
  const uniqueCategories = [
    ...new Set([
      ...Object.keys(categoryMeta),
      ...items.map((i) => i.category).filter(Boolean),
    ]),
  ];

  // ── New Category ───────────────────────────────────────────────

  const handleCreateCategory = async () => {
    if (!newCatNameEs.trim()) return;
    const key = newCatNameEs
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .trim()
      .replace(/\s+/g, '_');

    const price = parseFloat(newCatPrice) || 0;

    try {
      await setCategoryMeta(key, {
        name_es: newCatNameEs.trim(),
        name_en: newCatNameEn.trim() || newCatNameEs.trim(),
        display_order: Object.keys(categoryMeta).length + 1,
        icon: newCatIcon || '🍞',
        price,
      });
      showToast('✓ Categoría creada');
      setShowNewCategory(false);
      setNewCatNameEs('');
      setNewCatNameEn('');
      setNewCatIcon('🍞');
      setNewCatPrice('');
      loadCategoryMeta();
    } catch (err) {
      console.error('Error creating category:', err);
      showToast('Error: ' + err.message);
    }
  };

  // ── Edit Category Price ────────────────────────────────────────

  const handleEditCategoryPrice = (catKey) => {
    setEditingCatKey(catKey);
    setEditCatPrice(String(categoryMeta[catKey]?.price ?? 0));
  };

  const handleSaveCategoryPrice = async () => {
    if (!editingCatKey) return;
    const price = parseFloat(editCatPrice);
    if (isNaN(price) || price < 0) {
      showToast('Precio inválido');
      return;
    }
    try {
      const meta = categoryMeta[editingCatKey];
      await setCategoryMeta(editingCatKey, { ...meta, price });
      showToast('✓ Categoría actualizada');
      setEditingCatKey(null);
      setEditCatPrice('');
      loadCategoryMeta();
    } catch (err) {
      console.error('Error updating category:', err);
      showToast('Error: ' + err.message);
    }
  };

  // ── Render ─────────────────────────────────────────────────────

  // Side sheet for detail/edit — rendered on top of list
  const renderSideSheet = () => {
    if (!selectedBread) return null;
    return (
      <BreadCatalogDetail
        bread={selectedBread === 'new' ? null : selectedBread}
        categoryMeta={categoryMeta}
        onBack={handleBack}
        onSaved={handleSaved}
        onToast={showToast}
      />
    );
  };

  // Empty state
  if (!loading && items.length === 0) {
    return (
      <div className="bc-container">
        <div className="bc-page-header-section">
          <div className="bc-page-header">
            <div className="bc-breadcrumb">
              <span className="material-symbols-outlined">settings</span>
              Configuración de Productos
            </div>
            <h2 className="bc-page-title">Catálogo de Panadería</h2>
            <p className="bc-page-subtitle">
              Administra variedades, precios y disponibilidad de tu producción diaria.
            </p>
          </div>
        </div>
        <div className="bc-empty">
          <div className="bc-empty-icon">🍞</div>
          <div className="bc-empty-text">
            Agrega tu primer pan al catálogo
          </div>
          <button className="bc-empty-cta" onClick={handleNew} type="button">
            <span className="material-symbols-outlined">add</span>
            Nuevo Pan
          </button>
        </div>
        {renderSideSheet()}
        {toast && <div className="bc-toast">{toast}</div>}
      </div>
    );
  }

  // List view
  return (
    <div className="bc-container">
      {/* Page Header + CTA */}
      <div className="bc-page-header-section">
        <div className="bc-page-header">
          <div className="bc-breadcrumb">
            <span className="material-symbols-outlined">settings</span>
            Configuración de Productos
          </div>
          <h2 className="bc-page-title">Catálogo de Panadería</h2>
          <p className="bc-page-subtitle">
            Administra variedades, precios y disponibilidad de tu producción diaria.
          </p>
        </div>

        <div className="bc-cta-group">
          <button
            className="bc-btn-new-category"
            onClick={() => setShowNewCategory(!showNewCategory)}
            type="button"
          >
            <span className="material-symbols-outlined">category</span>
            Nueva Categoría
          </button>
          <button className="bc-btn-new-bread" onClick={handleNew} type="button">
            <span className="material-symbols-outlined">add</span>
            Nuevo Pan
          </button>
        </div>
      </div>

      {/* Inline New Category Form */}
      {showNewCategory && (
        <div className="bc-new-category-inline">
          <h3>Nueva Categoría</h3>
          <div className="bc-new-cat-fields">
            <input
              type="text"
              placeholder="Nombre en español"
              value={newCatNameEs}
              onChange={(e) => setNewCatNameEs(e.target.value)}
            />
            <input
              type="text"
              placeholder="English name"
              value={newCatNameEn}
              onChange={(e) => setNewCatNameEn(e.target.value)}
            />
            <input
              type="text"
              placeholder="Ícono (emoji)"
              value={newCatIcon}
              onChange={(e) => setNewCatIcon(e.target.value)}
              style={{ maxWidth: '120px' }}
            />
            <input
              type="number"
              placeholder="Precio (ej: 0.99)"
              value={newCatPrice}
              onChange={(e) => setNewCatPrice(e.target.value)}
              step="0.01"
              min="0"
              style={{ maxWidth: '150px' }}
            />
          </div>
          <div className="bc-new-cat-actions">
            <button
              className="bc-btn-create-cat"
              onClick={handleCreateCategory}
              type="button"
            >
              Crear Categoría
            </button>
            <button
              className="bc-btn-cancel-cat"
              onClick={() => setShowNewCategory(false)}
              type="button"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* Search & Filter Toolbar */}
      <div className="bc-toolbar-row">
        <div className="bc-search-wrapper">
          <span className="material-symbols-outlined bc-search-icon">search</span>
          <input
            className="bc-search-input"
            type="text"
            placeholder="Buscar pan..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              className="bc-search-clear"
              onClick={() => setSearch('')}
              type="button"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          )}
        </div>
        <select
          className="bc-category-filter"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="">Todas las categorías</option>
          {uniqueCategories.map((cat) => (
            <option key={cat} value={cat}>
              {categoryMeta[cat]?.name_es || cat}
            </option>
          ))}
        </select>
      </div>

      {/* Product List */}
      <BreadCatalogList
        items={items}
        categoryMeta={categoryMeta}
        search={search}
        categoryFilter={categoryFilter}
        onSelect={handleSelect}
        onToggleEnabled={handleToggleEnabled}
        onEditCategoryPrice={handleEditCategoryPrice}
        loading={loading}
      />

      {/* Category Edit Dialog (Price) */}
      {editingCatKey && (
        <div className="bc-price-edit-overlay" onClick={() => setEditingCatKey(null)}>
          <div className="bc-price-edit-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>Editar Categoría — {categoryMeta[editingCatKey]?.name_es || editingCatKey}</h3>
            <p className="bc-price-edit-hint">
              Todos los panes en esta categoría tendrán este precio.
            </p>
            <div className="bc-sheet-field">
              <label className="bc-sheet-field-label">Precio</label>
              <div className="bc-sheet-price-wrapper">
                <span className="bc-sheet-price-prefix">$</span>
                <input
                  className="bc-sheet-input bc-sheet-input-price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editCatPrice}
                  onChange={(e) => setEditCatPrice(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCategoryPrice(); }}
                />
              </div>
            </div>
            <div className="bc-price-edit-actions">
              <button className="bc-btn-create-cat" onClick={handleSaveCategoryPrice} type="button">
                Guardar
              </button>
              <button className="bc-btn-cancel-cat" onClick={() => setEditingCatKey(null)} type="button">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {renderSideSheet()}
      {toast && <div className="bc-toast">{toast}</div>}
    </div>
  );
}
